class MetaobjectPredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};

    this.predictiveSearchResults = this.querySelector(
      '[data-custom-predictive-search]'
    );

    this.defaultPageSearchResults = document.querySelector(
      '[data-custom-avis-list-default]'
    );

    this.pageSearchResults = document.querySelector('[data-custom-avis-list]');

    this.avisListContainer = document.querySelector(
      '[data-custom-avis-list-container]'
    );

    this.allPredictiveSearchInstances = document.querySelectorAll(
      'metaobject-predictive-search '
    );

    this.emptyText = this.getAttribute('data-empty-text');

    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.debounceTimeout = null;
    this.inputClear = this.querySelector('.custom-search__input-clear');

    this.setupEventListeners();
    this.isLink = this.getAttribute('data-link') === 'true';

    this.inputAvisLanding = this.querySelector('.avis-landing');

    // New logic to redirect on load if predictiveSearchResults exist
    if (this.inputAvisLanding) {
      const urlParams = new URLSearchParams(window.location.search);
      const searchTerm = urlParams.get('q');
      const formattedSearchTerm = searchTerm
        ? searchTerm.replace(/\s+/g, '-')
        : '';
      const queryKey = formattedSearchTerm.toLowerCase();
      this.inputAvisLanding.value = searchTerm;

      if (queryKey) {
        this.getSearchResults(queryKey);
      }
    }
  }

  setupEventListeners() {
    this.input = this.querySelector('.search__input');
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.input.addEventListener('input', this.onChange.bind(this));
    this.inputClear.addEventListener('click', this.onInputClear.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();

    if (this.inputClear) {
      this.inputClear.style.opacity = 1;
    }

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.setLiveRegionLoadingState();
    this.open();

    clearTimeout(this.debounceTimeout);

    this.debounceTimeout = setTimeout(() => {
      this.getSearchResults(this.searchTerm);
    }, 200); // Adjust the delay as needed
  }

  onInputClear() {
    if (this.inputClear) {
      this.inputClear.style.opacity = 0;
    }
    this.input.value = '';
    this.close(true);
    const url = new URL(window.location);
    url.search = ''; // Remove all search parameters
    window.history.pushState({}, '', url);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
    }

    event.preventDefault();
    const searchParams = new URLSearchParams({ q: this.searchTerm });

    window.location.href = `/pages/avis-de-deces?${searchParams.toString()}`;
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.avisListContainer) {
        if (!this.contains(document.activeElement)) this.close(true);
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close(true);
        break;
    }
  }

  switchOption(direction) {
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll('[role="option"]')
    ).filter((element) => element.offsetParent !== null);

    let activeElement = selectedElement || allVisibleElements[0];

    if (!activeElement) return;

    let index = allVisibleElements.indexOf(activeElement);
    let newIndex;

    if (moveUp) {
      newIndex = index > 0 ? index - 1 : allVisibleElements.length - 1;
    } else {
      newIndex = index < allVisibleElements.length - 1 ? index + 1 : 0;
    }

    if (activeElement) activeElement.setAttribute('aria-selected', false);

    activeElement = allVisibleElements[newIndex];

    if (activeElement) {
      activeElement.setAttribute('aria-selected', true);
      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const formattedSearchTerm = searchTerm.replace(/\s+/g, '-');
    const queryKey = formattedSearchTerm.toLowerCase();

    if (this.cachedResults[queryKey] && !this.avisListContainer) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Cancel any previous requests
    this.abortController.abort();
    this.abortController = new AbortController();

    let developmentModeUrl = window?.developmentMode?.url ? `${window?.developmentMode?.url}/api/shopify/avis/search?q=${queryKey}` : `https://yves-legare-server.vercel.app/api/shopify/avis/search?q=${queryKey}`;
    let developmentModeEnabled = window?.developmentMode?.enabled;
    let url = developmentModeEnabled ? developmentModeUrl : `https://yves-legare-server.vercel.app/api/shopify/avis/search?q=${queryKey}`;
    let targetStoreHeader = window?.targetStore?.enabled ? window?.targetStore?.value : null;
    let fetchOptions = {
      signal: this.abortController.signal
    };

    if (targetStoreHeader) {
      fetchOptions.headers = {
        'Content-Type': 'application/json',
        'x-target-store': targetStoreHeader
      };
    } 

    fetch(url, {
      ...fetchOptions
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.json();
      })
      .then((data) => {
        if (!data.success) {
          throw new Error(data.error || 'Search failed');
        }

        const resultsMarkup = this.generateResultsMarkup(data.results);
        const avisCardResultsMarkup = this.generateAvisCardResultsMarkup(
          data.results
        );

        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );

        this.renderSearchResults(resultsMarkup);

        if (this.predictiveSearchResults && this.defaultPageSearchResults) {
          this.renderPageSearchResults(avisCardResultsMarkup);
        }
      })
      .catch((error) => {
        if (error?.name === 'AbortError') {
          return;
        }
        this.close();
        throw error;
      });
  }

  generateAvisCardResultsMarkup(results) {
    if (!results.length) {
      return `
      <div class="custom-search__empty-state">
        <p class="tw-px-4 tw-py-2">${this.emptyText}</p>
      </div>
    `;
    }

    return results
      .map((result) => {
        const matchingAvis = window.avis.find(
          (avis) => avis.unique_id === result.contractId
        );

        const avisUrl = matchingAvis ? matchingAvis.avis_url : `/pages/avis-de-deces/${result.handle || ''}`;
        const imageContent = !result.error ? result?.image?.url : (window.themeVariables.avisImagePlaceholder ?? window.avisSvg);
        // const imageContent = matchingAvis ? matchingAvis.avis_image : (window.themeVariables.avisImagePlaceholder ?? window.avisSvg);

        const firstName = result.firstName || '';
        const lastName = result.lastName || '';
        const MaidenName = result.MaidenName || '';

        const birthYear = result.date_de_naissance
          ? result.date_de_naissance.split('-')[0]
          : '';
        const deathYear = result.date_de_deces
          ? result.date_de_deces.split('-')[0]
          : '';

        let fullName = '';

        if (MaidenName != '' && lastName != '') {
          fullName = `<span>${firstName} ${lastName} (née ${MaidenName})</span>`;
        } else if (lastName === '' && MaidenName != '') {
          fullName = `<span>${firstName} ${MaidenName}</span>`;
        } else if (lastName != '' && firstName != '') {
          fullName = `<span>${firstName} ${lastName}</span>`;
        }

        return `
          <div class="avis-marquee-card">
            <a href="${avisUrl}" class="avis-marquee-card__content">
              <div class="avis-marquee-card__content-image-wrapper tw-relative">
                <img
                    src="${imageContent}"
                    alt=""
                    width="304"
                    height="296"
                    class="avis-marquee-card__img tw-filter tw-grayscale grey-scale"
                >
                <div class="avis__quick-view tw-absolute tw-bottom-4 tw-left-4 tw-z-10">
                  ${window.iconEye}
                </div>
              </div>
              <div class="avis-marquee-card__content-body tw-flex tw-flex-col">
                <div class="tw-flex tw-flex-wrap tw-gap-2">
                  <h5 class="tw-font-bold tw-mt-4 tw-text-left tw-capitalize tw-font-heading">
                    ${fullName}
                  </h5>
                </div>
                <div class="tw-flex tw-gap-1">
                  ${birthYear ? `<p>${birthYear}</p><span>-</span>` : ''}
                  ${deathYear ? `<p>${deathYear}</p>` : ''}
                </div>
              </div>
            </a>
          </div>
        `;
      })
      .join('');
  }

  generateResultsMarkup(results) {
    if (!results.length) {
      return `
        <div class="custom-search__empty-state">
          <p class="custom-search__empty-state-text tw-px-4 tw-py-2">${this.emptyText}</p>
        </div>
      `;
    }

    const resultsHtml = results
      .map((result) => {
        // console.log(result);
        const matchingAvis = window.avis.find(
          (avis) => avis.unique_id === result.contractId
        );
        const avisUrl = matchingAvis ? matchingAvis.avis_url : `/pages/avis-de-deces/${result.handle || ''}`;
        const imageContent = result.image?.url ?? (matchingAvis ? matchingAvis.avis_image : (window.themeVariables.avisImagePlaceholder ?? window.avisSvg));

        let image = `<img class="tw-aspect-square tw-object-cover" src="${imageContent}" alt="${result.firstName} ${result.lastName}" width="28" height="27">`;

        let dates = '';
        if (result && result.birthDate && result.deathDate) {
          let ddn = result.birthDate.split('-');
          let ddd = result.deathDate.split('-');
          dates = `${ddn[0]} - ${ddd[0]}`;
        }

        let expoDay = window.themeVariables.strings.avisNoDate;
        let expoHour = window.themeVariables.strings.avisNoDate;
        if (result && result.expoDates) {
          let expoDates = result.expoDates.split('||');
          if (expoDates.length > 0) {
            let day_hours = expoDates[0].split('**');
            expoDay = day_hours[0];
            expoHour = day_hours[1];
          }
        }

        if (this.isLink) {
          return `
            <a href=${avisUrl} 
              class="custom-search__result-card tw-flex tw-gap-2 tw-items-center tw-py-2 tw-px-4"
              data-avis-option
              data-avis-id="${result?.id}"
              data-contrat="${result.contractId}"
              data-private="${result.expoPrivate}"
              data-complexe="${result.expoComplex}"
              data-address="${result.expoAddress}"
              data-expoday="${expoDay}"
              data-expohour="${expoHour}"
              data-dates="${dates}"
              data-image="${imageContent}"
              data-name="${result.firstName} ${result.lastName}"
            >
              ${image}
              <p class="custom-search__result-card-text">${result.firstName} ${result.lastName}</p>
            </a>
          `;
        }

        return `
          <li class="custom-search__result-card tw-flex tw-gap-2 tw-items-center tw-py-2 tw-px-4"
            data-avis-option
            data-avis-id="${result?.id}"
            data-contrat="${result.contractId}"
            data-private="${result.expoPrivate}"
            data-complexe="${result.expoComplex}"
            data-address="${result.expoAddress}"
            data-expoday="${expoDay}"
            data-expohour="${expoHour}"
            data-dates="${dates}"
            data-image="${imageContent}"
            data-name="${result.firstName} ${result.lastName}"
          >
            ${image}
            <p class="custom-search__result-card-text">${result.firstName} ${result.lastName}</p>
          </li>
        `;
      })
      .join('');

    return `
      <div id="custom-search-results-wrapper" class="custom-search__results-wrapper">
        ${resultsHtml}
      </div>
    `;
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector('.custom-predictive-search-status');
    this.loadingText =
      this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);

    if (this.avisListContainer) {
      this.avisListContainer.setAttribute('loading', true);
    }
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) {
      return;
    }
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  renderPageSearchResults(resultsMarkup) {
    if (!this.pageSearchResults) {
      return;
    }

    this.pageSearchResults.innerHTML = resultsMarkup;
    this.avisListContainer.setAttribute('results', true);

    this.setLiveRegionResults();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    if (this.avisListContainer) {
      this.avisListContainer.removeAttribute('loading');
    }

    const resultsCount =
      this.querySelector('#predictive-search-results-list')?.children.length ||
      0;
    this.setLiveRegionText(`${resultsCount} results found`);
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      if (this.avisListContainer && this.pageSearchResults) {
        this.pageSearchResults.innerHTML = '';
        this.avisListContainer.removeAttribute('results');
      }
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');

    if (this.avisListContainer) {
      this.avisListContainer.removeAttribute('loading');
    }

    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.isOpen = false;
  }
}

customElements.define(
  'metaobject-predictive-search',
  MetaobjectPredictiveSearch
);
